import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API } from '../App';
import { Eye, EyeOff } from 'lucide-react';

function AuthPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [requireMFA, setRequireMFA] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    organization: '',
    role: 'individual',
    phone: '',
    mfa_code: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password,
          mfa_code: formData.mfa_code || undefined
        });

        if (response.data.require_mfa) {
          setRequireMFA(true);
          toast.success('Please enter your MFA code');
          return;
        }

        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success('Login successful!');
        
        if (response.data.user.role === 'admin') {
          navigate('/admin');
        } else if (response.data.user.role === 'police') {
          navigate('/police');
        } else {
          navigate('/dashboard');
        }
      } else {
        const response = await axios.post(`${API}/auth/register`, formData);
        toast.success('Registration successful! Please login.');
        
        if (response.data.mfa_secret) {
          toast.success(`MFA Secret: ${response.data.mfa_secret} (Save this!)`, {
            duration: 10000
          });
        }
        
        setIsLogin(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="auth-page" data-testid="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/hakicheck-logo.png" alt="HakiCheck" className="auth-logo-img" />
          </div>
          <p className="auth-subtitle">
            {isLogin ? 'Welcome back! Sign in to your account' : 'Create your account to get started'}
          </p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} data-testid="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              data-testid="email-input"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="you@example.com"
            />
          </div>

          {!isLogin && (
            <>
              <div className="form-group">
                <label htmlFor="full_name">Full Name</label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  data-testid="fullname-input"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label htmlFor="role">Account Type</label>
                <select
                  id="role"
                  name="role"
                  data-testid="role-select"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  <option value="individual">Domestic Hiring</option>
                  <option value="corporate">Corporate Entities</option>
                  <option value="financial">Financial Institutions</option>
                  <option value="police">Law Enforcement</option>
                  <option value="admin">System Administrator</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="organization">Organization (Optional)</label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  data-testid="organization-input"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder="Your Company Name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number (Optional)</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  data-testid="phone-input"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+234 xxx xxx xxxx"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                data-testid="password-input"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="••••••••"
              />
              <button
                type="button"
                className="password-toggle"
                data-testid="toggle-password-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {requireMFA && (
            <div className="form-group">
              <label htmlFor="mfa_code">MFA Code</label>
              <input
                type="text"
                id="mfa_code"
                name="mfa_code"
                data-testid="mfa-code-input"
                value={formData.mfa_code}
                onChange={handleChange}
                placeholder="Enter 6-digit code"
                maxLength="6"
              />
            </div>
          )}

          <button type="submit" className="auth-submit" data-testid="auth-submit-btn">
            {isLogin ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {isLogin ? "Don't have an account?" : 'Already have an account?'}
            <button
              className="auth-switch"
              data-testid="auth-switch-btn"
              onClick={() => {
                setIsLogin(!isLogin);
                setRequireMFA(false);
              }}
            >
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </div>
      </div>

      <style>{`
        .auth-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 2rem;
        }

        .auth-container {
          background: white;
          padding: 3rem;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          max-width: 500px;
          width: 100%;
        }

        .auth-header {
          text-align: center;
          margin-bottom: 2.5rem;
        }

        .auth-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          padding: 2rem;
          background: linear-gradient(135deg, #5a738f 0%, #4a5f7f 100%);
          border-radius: 15px;
          margin-left: -3rem;
          margin-right: -3rem;
          margin-top: -3rem;
        }

        .auth-logo-img {
          height: 80px;
          width: auto;
          filter: brightness(1.1);
        }

        .auth-subtitle {
          color: #6c757d;
          font-size: 0.95rem;
        }

        .auth-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          font-weight: 600;
          color: #212529;
          font-size: 0.9rem;
        }

        .form-group input,
        .form-group select {
          padding: 0.85rem 1rem;
          border: 2px solid #e9ecef;
          border-radius: 10px;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #5a738f;
        }

        .password-input {
          position: relative;
          display: flex;
          align-items: center;
        }

        .password-input input {
          flex: 1;
          padding-right: 3rem;
        }

        .password-toggle {
          position: absolute;
          right: 1rem;
          background: none;
          border: none;
          cursor: pointer;
          color: #6c757d;
          padding: 0.5rem;
          display: flex;
          align-items: center;
        }

        .auth-submit {
          padding: 1rem;
          background: linear-gradient(135deg, #5a738f 0%, #4a5f7f 100%);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          margin-top: 0.5rem;
        }

        .auth-submit:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(90, 115, 143, 0.3);
        }

        .auth-footer {
          text-align: center;
          margin-top: 2rem;
          color: #6c757d;
          font-size: 0.9rem;
        }

        .auth-switch {
          background: none;
          border: none;
          color: #5a738f;
          font-weight: 600;
          cursor: pointer;
          margin-left: 0.5rem;
          text-decoration: underline;
        }

        @media (max-width: 640px) {
          .auth-container {
            padding: 2rem;
          }
          
          .auth-logo-img {
            height: 60px;
          }
        }
      `}</style>
    </div>
  );
}

export default AuthPage;
